import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FileQuestion, ClipboardList, TrendingUp, BookOpen } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TeacherHome = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState({
    totalQuestions: 0,
    categories: [],
    difficulties: { easy: 0, medium: 0, hard: 0 }
  });
  const [examsCount, setExamsCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [questionsStats, examsResponse] = await Promise.all([
        axios.get(`${API}/questions/stats`),
        axios.get(`${API}/exams`)
      ]);
      
      setStats({
        totalQuestions: questionsStats.data.total,
        categories: questionsStats.data.categories,
        difficulties: questionsStats.data.difficulties
      });
      setExamsCount(examsResponse.data.length);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: t('totalQuestions'),
      value: stats.totalQuestions,
      icon: FileQuestion,
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'bg-blue-50'
    },
    {
      title: t('exams'),
      value: examsCount,
      icon: ClipboardList,
      color: 'from-indigo-500 to-purple-500',
      bgColor: 'bg-indigo-50'
    },
    {
      title: t('categories'),
      value: stats.categories.length,
      icon: BookOpen,
      color: 'from-green-500 to-emerald-500',
      bgColor: 'bg-green-50'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="teacher-home">
      <div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2">{t('dashboard')}</h2>
        <p className="text-gray-600">{t('statistics')}</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card
              key={index}
              className="card-hover border-0 shadow-lg overflow-hidden"
            >
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium text-gray-600">
                    {stat.title}
                  </CardTitle>
                  <div className={`p-3 rounded-xl ${stat.bgColor}`}>
                    <Icon className={`h-6 w-6 bg-gradient-to-br ${stat.color} bg-clip-text text-transparent`} />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className={`text-4xl font-bold bg-gradient-to-r ${stat.color} bg-clip-text text-transparent`}>
                  {stat.value}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Difficulty Distribution */}
      <Card className="shadow-lg border-0">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            {t('difficulty')} {t('statistics')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Object.entries(stats.difficulties).map(([level, count]) => {
              const percentage = stats.totalQuestions > 0 
                ? (count / stats.totalQuestions) * 100 
                : 0;
              
              const colors = {
                easy: { bg: 'bg-green-500', text: 'text-green-700', light: 'bg-green-100' },
                medium: { bg: 'bg-yellow-500', text: 'text-yellow-700', light: 'bg-yellow-100' },
                hard: { bg: 'bg-red-500', text: 'text-red-700', light: 'bg-red-100' }
              };

              return (
                <div key={level} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className={`font-medium ${colors[level].text} capitalize`}>
                      {t(level)}
                    </span>
                    <span className="text-sm text-gray-600">
                      {count} ({percentage.toFixed(1)}%)
                    </span>
                  </div>
                  <div className={`h-3 ${colors[level].light} rounded-full overflow-hidden`}>
                    <div
                      className={`h-full ${colors[level].bg} rounded-full transition-all duration-500`}
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Categories List */}
      {stats.categories.length > 0 && (
        <Card className="shadow-lg border-0">
          <CardHeader>
            <CardTitle>{t('categories')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {stats.categories.map((category, index) => (
                <div
                  key={index}
                  className="px-4 py-2 bg-gradient-to-r from-indigo-100 to-cyan-100 text-indigo-700 rounded-full font-medium text-sm"
                >
                  {category}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TeacherHome;
